// Session renewal for the user plane (task remember_me, ADR 0190).
//
// A "stay signed in" session runs up to 365 days but its access token is
// capped at 24 hours, so the browser has to trade the refresh cookie for a
// fresh pair. Doing that only on the mount probe is not enough: a dashboard
// left open across the 24-hour boundary — a kiosk on a boat, a pinned tab —
// keeps making authenticated calls from an already-mounted app, and every one
// of them would 401 against a session that is still perfectly valid. So
// renewal belongs in the API layer, underneath every call, not in one probe.
//
// Two rules make that safe:
//
//   1. SINGLE-FLIGHT. Rotation is single-use server-side (one atomic claim,
//      exactly one winner), so a burst of parallel calls must produce ONE
//      renewal, not one each — otherwise the first would win and the rest
//      would 401 themselves into a spurious sign-out.
//   2. OPT-IN. The admin console shares this fetch wrapper but is a separate
//      security domain with its own cookie family and no refresh route
//      (ADR 0040). It never enables renewal, so its 401s stay 401s.
//
// Deliberately does NOT import ../api: the renewal POST is a bare fetch, which
// keeps the module free of a cycle and makes it structurally impossible for a
// renewal to recurse into itself.

let renewUrl: string | null = null;
let inFlight: Promise<boolean> | null = null;
let unloading = false;

// Rotation is single-use, so a renewal whose RESPONSE is thrown away spends
// the session's refresh token for nothing: the server claimed the old jti and
// set a new cookie pair the browser never stored, and the next page load
// presents a token that is already dead. A document being torn down is exactly
// where that happens — a poll answered 401 while the user is already
// navigating would start a rotation it cannot live to finish, and the page
// that replaces it inherits a burned session.
//
// So we stop renewing the moment the page starts to go away. `pageshow` undoes
// it for a document restored from the back/forward cache, which is alive again
// and must be able to renew.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    unloading = true;
  });
  window.addEventListener('pageshow', () => {
    unloading = false;
  });
}

/**
 * Turn on renewal for this app, naming the endpoint that rotates the session.
 * Called by the user-side auth provider; the admin provider never calls it.
 */
export function enableSessionRenewal(url: string): void {
  renewUrl = url;
}

/** Test seam — drops the endpoint, any in-flight renewal, and the unload flag. */
export function resetSessionRenewal(): void {
  renewUrl = null;
  inFlight = null;
  unloading = false;
}

/** Test seam — stand in for the `pagehide` the browser fires on teardown. */
export function markPageUnloading(): void {
  unloading = true;
}

export function sessionRenewalEnabled(): boolean {
  return renewUrl !== null;
}

/**
 * Endpoints renewal must never fire for.
 *
 * `/v1/auth/*` are the CREDENTIAL endpoints: a 401 from them is a statement
 * about the credentials just presented, not about an aged access cookie.
 * Renewing there is worse than useless — a wrong-password `POST
 * /v1/auth/login` would be retried, and each retry is another failed attempt
 * against the per-account lockout counter and the per-IP step-up challenge.
 * `/v1/admin/*` belongs to the other plane.
 */
export function isRenewablePath(path: string): boolean {
  return !path.startsWith('/v1/auth/') && !path.startsWith('/v1/admin/');
}

/**
 * Rotate the session, at most once concurrently. Resolves true when the
 * caller may retry, false when the session could not be renewed.
 *
 * Never rejects: a renewal failure is an ordinary outcome (the session really
 * did end), and callers decide what to do about the original request.
 */
export async function renewSession(): Promise<boolean> {
  const url = renewUrl;
  if (url === null) return false;
  if (unloading) return false;
  // Everyone who arrives while a renewal is in flight waits on that one.
  inFlight ??= (async () => {
    try {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        // Let a renewal already in flight when the page goes away finish
        // anyway, so its Set-Cookie still lands and the incoming document
        // inherits a live session instead of a spent token. The `unloading`
        // guard above stops us STARTING one; this covers the request that was
        // already on the wire when the navigation began.
        keepalive: true,
      });
      return res.ok;
    } catch {
      // Offline or a dropped connection — not a dead session.
      return false;
    } finally {
      // Cleared before the promise resolves, so the next 401 after this
      // settles starts a fresh renewal rather than replaying a stale verdict.
      inFlight = null;
    }
  })();
  return inFlight;
}
